/**
 * calendar.js — ICS file generation + Google Calendar link builder
 * Used for Fhysique Factory consultation booking confirmations.
 */

const LOCATION = '6-8 Parkington Plaza, Maths Unlimited Building, Kingston, Jamaica';

/**
 * Format a JS Date to the iCalendar YYYYMMDDTHHMMSS format (local time, no Z).
 */
function toICSDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    date.getFullYear() +
    pad(date.getMonth() + 1) +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    pad(date.getMinutes()) +
    '00'
  );
}

/**
 * Build a .ics file string for a 1-hour consultation event.
 * @param {string} clientName
 * @param {string} clientEmail
 * @param {string} refCode    unique client ID
 * @param {Date}   startDate  JS Date object for start of session
 */
export function buildICS({ clientName, clientEmail, refCode, startDate }) {
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // +1 hour
  const uid = `${refCode}-${Date.now()}@fhysiquefactory.com`;

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//The Fhysique Factory//Consultation Booking//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:REQUEST',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${toICSDate(new Date())}`,
    `DTSTART:${toICSDate(startDate)}`,
    `DTEND:${toICSDate(endDate)}`,
    'SUMMARY:Fhysique Factory — 1-on-1 Consultation with Ramone',
    `LOCATION:${LOCATION}`,
    `DESCRIPTION:Your session with Ramone Fearon.\\n\\nClient ID: ${refCode}\\nShow this ID to Ramone when you arrive.\\n\\nLocation: ${LOCATION}`,
    `ORGANIZER;CN=The Fhysique Factory:mailto:info@fhysiquefactory.com`,
    clientEmail ? `ATTENDEE;RSVP=TRUE;CN=${clientName}:mailto:${clientEmail}` : '',
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'BEGIN:VALARM',
    'TRIGGER:-PT1H',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder: Fhysique Factory consultation in 1 hour',
    'END:VALARM',
    'BEGIN:VALARM',
    'TRIGGER:-PT24H',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder: Fhysique Factory consultation tomorrow',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter(Boolean)
    .join('\r\n');
}

/**
 * Trigger a browser download of the .ics file.
 */
export function downloadICS(icsContent, filename = 'fhysique-factory-consultation.ics') {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Build a "Add to Google Calendar" link for a 1-hour consultation.
 * No API key required — opens Google Calendar in the browser.
 */
export function buildGoogleCalendarLink({ clientName, refCode, startDate }) {
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

  const fmt = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return (
      d.getFullYear() +
      pad(d.getMonth() + 1) +
      pad(d.getDate()) +
      'T' +
      pad(d.getHours()) +
      pad(d.getMinutes()) +
      '00'
    );
  };

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: 'Fhysique Factory — 1-on-1 Consultation with Ramone',
    dates: `${fmt(startDate)}/${fmt(endDate)}`,
    location: LOCATION,
    details: `Your private 60-minute consultation with Ramone Fearon at The Fhysique Factory.\n\nYour Client ID: ${refCode}\nShow this to Ramone when you arrive.\n\nLocation: ${LOCATION}`,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Build available consultation time slots for a given date string (YYYY-MM-DD).
 * Excludes weekends. Returns an array of { label, value (ISO string) }.
 */
export const CONSULTATION_TIMES = [
  '09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00',
];

export function getAvailableSlots(dateStr) {
  if (!dateStr) return [];
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay(); // 0=Sun, 6=Sat
  if (day === 0 || day === 6) return []; // no weekends

  return CONSULTATION_TIMES.map((t) => {
    const dt = new Date(dateStr + 'T' + t + ':00');
    const label = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return { label, value: dt.toISOString(), time: t };
  });
}

/**
 * Get the earliest bookable date (tomorrow, skip weekends).
 */
export function getMinBookingDate() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}
